import type { AppType } from "@vetbolso/api/app";
import type { Sessao } from "@vetbolso/contracts";
import { hc } from "hono/client";
import type { MapaMental } from "./mapas-dados.ts";

/** Mesmo domínio que a API (proxy em dev, nginx em produção): o cookie de sessão segue sozinho. */
export const v1 = hc<AppType>("/", { init: { credentials: "same-origin" } }).api.v1;

export class ErroApi extends Error {
  constructor(
    readonly status: number,
    readonly codigo: string,
    msg: string,
  ) {
    super(msg);
  }
}

/** Sem rede (ou servidor fora): o app cai para a base salva. */
export class SemRede extends Error {}

async function corpo(r: Response) {
  return (await r.json().catch(() => ({}))) as { erro?: string; msg?: string; message?: string; versao?: string };
}

export async function lerSessao(): Promise<Sessao> {
  let r: Response;
  try {
    r = await v1.sessao.$get();
  } catch {
    throw new SemRede();
  }
  if (r.status >= 500) throw new SemRede();
  if (!r.ok) {
    const b = await corpo(r);
    throw new ErroApi(r.status, b.erro ?? "erro", b.msg ?? b.erro ?? `erro ${r.status}`);
  }
  return (await r.json()) as Sessao;
}

export async function salvarPerfil(b: { perfil?: Sessao["usuario"]["perfil"] & string; nome?: string; cpf?: string }): Promise<Sessao> {
  const r = await v1.perfil.$patch({ json: b });
  if (!r.ok) {
    const c = await corpo(r);
    throw new ErroApi(
      r.status,
      c.erro ?? "erro",
      c.erro === "cpf_invalido" ? "CPF inválido. Confira os 11 dígitos." : (c.msg ?? "não foi possível salvar"),
    );
  }
  return (await r.json()) as Sessao;
}

/** Coleção de uma release. 409 quer dizer que outra release entrou no meio do download. */
export async function baixarColecao(nome: string, release: string): Promise<unknown> {
  let r: Response;
  try {
    r = await fetch(`/api/v1/colecoes/${encodeURIComponent(nome)}?release=${encodeURIComponent(release)}`, { credentials: "same-origin" });
  } catch {
    throw new SemRede();
  }
  if (!r.ok) {
    const b = await corpo(r);
    throw new ErroApi(r.status, b.erro ?? "erro", b.erro ?? `erro ${r.status}`);
  }
  return r.json();
}

/** Liga o checkout à conta. Se falhar, a compra segue e só perde a ligação. */
export function registrarCheckout(b: {
  visita: string;
  url: string;
  fbp?: string;
  fbc?: string;
  dados: { content_name: string; currency: "BRL"; value?: number };
}) {
  void fetch("/api/v1/checkout", {
    method: "POST",
    keepalive: true,
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(b),
  }).catch(() => {});
}

/** Mapas do módulo pago, ou `null` se a cópia do aparelho já é a atual. */
export async function baixarMapas(versao?: string): Promise<{ versao: string; dados: MapaMental[] } | null> {
  let r: Response;
  try {
    r = await fetch("/api/v1/mapas", { credentials: "same-origin", headers: versao ? { "if-none-match": `"${versao}"` } : {} });
  } catch {
    throw new SemRede();
  }
  if (r.status === 304) return null;
  if (!r.ok) {
    const b = await corpo(r);
    throw new ErroApi(r.status, b.erro ?? "erro", b.erro ?? `erro ${r.status}`);
  }
  return { versao: (r.headers.get("etag") ?? "").replaceAll('"', ""), dados: (await r.json()) as MapaMental[] };
}

async function auth(caminho: string, b: unknown) {
  let r: Response;
  try {
    r = await fetch(`/api/auth${caminho}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(b),
    });
  } catch {
    throw new SemRede("sem conexão");
  }
  if (!r.ok) {
    const c = await corpo(r);
    throw new ErroApi(r.status, "auth", c.message ?? "não foi possível continuar");
  }
}
export const pedirCodigo = (email: string) => auth("/email-otp/send-verification-otp", { email, type: "sign-in" });
export const entrarComCodigo = (email: string, otp: string) => auth("/sign-in/email-otp", { email, otp });
export const sair = () => auth("/sign-out", {});
